import { Entity, ImpactedCompany } from '@api';
import { SkeletonLoader } from '@design';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import React from 'react';

import { PrimaryCompanyCard } from './components/company-card/PrimaryCompanyCard';
import { SecondaryCompanyCard } from './components/company-card/SecondaryCompanyCard';
import styles from './ImpactedCompanies.module.scss';

export interface ImpactedCompaniesProps {
  impactedCompanies: ImpactedCompany[];
  source: PosthogTrackingSource;
  variant?: 'primary' | 'secondary';
  title: string;
  insightCreatedAt?: string;
  hideRelationship?: boolean;
}

export const ImpactedCompanies = ({
  impactedCompanies,
  source,
  variant = 'primary',
  title,
  insightCreatedAt,
  hideRelationship = false,
}: ImpactedCompaniesProps) => {
  const entityTags = impactedCompanies.map(c => c.entity_tag);
  const { data: entities, isLoading } = useEntities({
    entityTags,
    enabled: !!entityTags.length,
  });

  const companiesWithEntities = impactedCompanies.reduce((acc, company) => {
    const entity = entities?.find(e => e.tag === company.entity_tag);

    if (entity) {
      acc.push({ company, entity });
    }

    return acc;
  }, [] as { company: ImpactedCompany; entity: Entity }[]);

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '100px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!companiesWithEntities.length) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.companiesWrapper}>
        {companiesWithEntities.map(company => {
          if (variant === 'primary') {
            return (
              <PrimaryCompanyCard
                key={company.company.entity_tag}
                company={company.company}
                entity={company.entity}
                source={source}
                hideRelationship={hideRelationship}
              />
            );
          }

          return (
            <SecondaryCompanyCard
              key={company.company.entity_tag}
              company={company.company}
              entity={company.entity}
              source={source}
              referenceDate={insightCreatedAt}
              hideRelationship={hideRelationship}
            />
          );
        })}
      </div>
    </div>
  );
};
